// The rate question WellSky asks, answered while approving the assignment.
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { type EhrRatePicker } from '@apps/web/features/phoebe_chat_v3/model/shift_write_card';
import {
    CUSTOM_CAREGIVER_RATE_TYPES,
    type CustomCaregiverRateType,
    getRateCategoryDisplayName,
    isValidCustomCaregiverRateAmount,
    toCustomCaregiverRateType,
} from '@apps/web/lib/wellsky_rates';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';

// One rate question, two ways to key it: an existing visit, or the client and
// window a create is about to book. Callers pass the picker and never learn
// which route answered.
function useClientRates(
    organizationId: string,
    picker: EhrRatePicker,
    asks: boolean,
) {
    const byShift = picker.shift_id !== null;
    const pending =
        picker.client_id !== null &&
        picker.starts_at !== null &&
        picker.ends_at !== null;
    const shiftQuery = useApiQuery({
        path: '/organizations/{organization_id}/shifts/{shift_id}/wellsky-client-rate-info',
        method: 'get',
        pathParams: byShift
            ? {
                  organization_id: organizationId,
                  shift_id: picker.shift_id as string,
              }
            : undefined,
        enabled: asks && byShift,
        reason: 'v3-assignment-client-rate',
        select: (response) => response,
    });
    const clientQuery = useApiQuery({
        path: '/organizations/{organization_id}/clients/{client_id}/wellsky-client-rate-info',
        method: 'get',
        pathParams:
            !byShift && pending
                ? {
                      organization_id: organizationId,
                      client_id: picker.client_id as string,
                  }
                : undefined,
        queryParams:
            !byShift && pending
                ? {
                      starts_at: picker.starts_at as string,
                      ends_at: picker.ends_at as string,
                  }
                : undefined,
        enabled: asks && !byShift && pending,
        reason: 'v3-create-client-rate',
        select: (response) => response,
    });
    return byShift ? shiftQuery : clientQuery;
}

function useCaregiverRates(
    organizationId: string,
    picker: EhrRatePicker,
    clientRateId: string | null,
) {
    const ready = clientRateId !== null;
    const byShift = picker.shift_id !== null;
    const pending =
        picker.client_id !== null &&
        picker.starts_at !== null &&
        picker.ends_at !== null;
    const shiftQuery = useApiQuery({
        path: '/organizations/{organization_id}/shifts/{shift_id}/wellsky-caregiver-rate-info',
        method: 'get',
        pathParams:
            byShift && ready
                ? {
                      organization_id: organizationId,
                      shift_id: picker.shift_id as string,
                  }
                : undefined,
        queryParams:
            byShift && ready
                ? {
                      client_rate_id: clientRateId,
                      caregiver_id: picker.caregiver_id,
                  }
                : undefined,
        enabled: byShift && ready,
        reason: 'v3-assignment-caregiver-rate',
        select: (response) => response,
    });
    const clientQuery = useApiQuery({
        path: '/organizations/{organization_id}/clients/{client_id}/wellsky-caregiver-rate-info',
        method: 'get',
        pathParams:
            !byShift && pending && ready
                ? {
                      organization_id: organizationId,
                      client_id: picker.client_id as string,
                  }
                : undefined,
        queryParams:
            !byShift && pending && ready
                ? {
                      caregiver_id: picker.caregiver_id,
                      client_rate_id: clientRateId,
                      starts_at: picker.starts_at as string,
                      ends_at: picker.ends_at as string,
                  }
                : undefined,
        enabled: !byShift && pending && ready,
        reason: 'v3-create-caregiver-rate',
        select: (response) => response,
    });
    return byShift ? shiftQuery : clientQuery;
}

// A custom amount replaces the rate rather than adding to it, so it carries
// its own type and no id — the same contract the assign modal reports.
type RatePick = Readonly<{
    readonly id?: string;
    readonly type: number;
    readonly amount?: string;
}>;

export type EhrRatePicks = Readonly<{
    readonly client?: RatePick;
    readonly caregiver?: RatePick;
}>;

type RateKey = 'client' | 'caregiver';

type CustomState = Readonly<{
    readonly on: boolean;
    readonly type: CustomCaregiverRateType;
    readonly amount: string;
}>;

const NO_CUSTOM: CustomState = { on: false, type: '1', amount: '' };

// WellSky keys a caregiver rate by the client rate it is priced against, and
// accepts this sentinel when the client side carries an amount instead.
const CUSTOM_CLIENT_RATE_LOOKUP_ID = 'custom';

// WellSky prices per unit, so $375 live-in and $28 hourly are not
// comparable. The row shows the unit for the same reason its own UI does.
const UNITS: Record<number, string> = {
    1: 'per hour',
    2: 'per visit',
    3: 'per live-in day',
};

const AMOUNT_LABELS: Record<RateKey, string> = {
    client: 'Custom client bill rate',
    caregiver: 'Custom caregiver pay rate',
};

type Category = Readonly<{
    id: string;
    name: string;
    type: number;
    display_name?: string | null;
}>;

function asValue(category: Category | null | undefined): string | null {
    return category ? `${category.id}:${category.type}` : null;
}

function optionsOf(categories: ReadonlyArray<Category> | undefined) {
    return (categories ?? []).map((category) => ({
        value: `${category.id}:${category.type}`,
        label: getRateCategoryDisplayName(category),
        detail: UNITS[category.type] ?? null,
    }));
}

function parse(value: string): Readonly<{ id: string; type: number }> | null {
    const separator = value.lastIndexOf(':');
    const id = value.slice(0, separator);
    const type = Number(value.slice(separator + 1));
    return id && Number.isInteger(type) ? { id, type } : null;
}

function pickOf(value: string, custom: CustomState): RatePick | null {
    const typed = custom.amount.trim();
    if (custom.on) {
        return isValidCustomCaregiverRateAmount(typed)
            ? { type: Number(custom.type), amount: typed }
            : null;
    }
    return value ? parse(value) : null;
}

export function EhrRatePickerField({
    picker,
    organizationId,
    disabled,
    onChange,
}: Readonly<{
    picker: EhrRatePicker;
    organizationId: string;
    disabled: boolean;
    onChange: (picks: EhrRatePicks | null) => void;
}>): ReactNode {
    const [values, setValues] = useState<Record<RateKey, string>>({
        client: '',
        caregiver: '',
    });
    const [open, setOpen] = useState<Record<RateKey, boolean>>({
        client: false,
        caregiver: false,
    });
    const [search, setSearch] = useState<Record<RateKey, string>>({
        client: '',
        caregiver: '',
    });
    const [custom, setCustom] = useState<Record<RateKey, CustomState>>({
        client: NO_CUSTOM,
        caregiver: NO_CUSTOM,
    });

    const asksClient = picker.rates.some((rate) => rate.key === 'client');
    const asksCaregiver = picker.rates.some((rate) => rate.key === 'caregiver');

    const clientRates = useClientRates(organizationId, picker, asksClient);
    const clientPick = values.client ? parse(values.client) : null;
    // The caregiver rate is priced against a client rate: the one just
    // picked, the custom sentinel when the client carries an amount, or the
    // one this write already settled.
    const clientRateId = custom.client.on
        ? CUSTOM_CLIENT_RATE_LOOKUP_ID
        : (clientPick?.id ?? picker.known_client_rate_id);
    const caregiverRates = useCaregiverRates(
        organizationId,
        picker,
        asksCaregiver ? (clientRateId ?? null) : null,
    );

    // WellSky resolves most clients itself: when it names a default, the
    // card shows that rate already chosen instead of asking again.
    const clientDefault = asValue(clientRates.data?.default_rate_category);
    useEffect(() => {
        setValues((current) =>
            asksClient && clientDefault !== null && current.client === ''
                ? { ...current, client: clientDefault }
                : current,
        );
    }, [asksClient, clientDefault]);
    const caregiverDefault = asValue(
        caregiverRates.data?.default_rate_category,
    );
    const caregiverCustomOn = custom.caregiver.on;
    useEffect(() => {
        setValues((current) =>
            caregiverDefault !== null &&
            current.caregiver === '' &&
            !caregiverCustomOn
                ? { ...current, caregiver: caregiverDefault }
                : current,
        );
    }, [caregiverDefault, caregiverCustomOn]);

    // WellSky keys caregiver rates by the client rate, so changing the
    // client rate invalidates a caregiver rate picked against the old one.
    const clientCustomOn = custom.client.on;
    const clientValue = values.client;
    useEffect(() => {
        setValues((current) => ({ ...current, caregiver: '' }));
        setSearch((current) => ({ ...current, caregiver: '' }));
    }, [clientValue, clientCustomOn]);

    const optionsByKey: Record<RateKey, ReturnType<typeof optionsOf>> = useMemo(
        () => ({
            client: optionsOf(clientRates.data?.rate_categories),
            caregiver: optionsOf(caregiverRates.data?.rate_categories),
        }),
        [clientRates.data, caregiverRates.data],
    );

    const clientCustom = custom.client;
    const caregiverCustom = custom.caregiver;
    const caregiverValue = values.caregiver;
    useEffect(() => {
        const client = pickOf(clientValue, clientCustom);
        const caregiver = pickOf(caregiverValue, caregiverCustom);
        if (
            (asksClient && client === null) ||
            (asksCaregiver && caregiver === null)
        ) {
            onChange(null);
            return;
        }
        onChange({
            ...(client === null ? {} : { client }),
            ...(caregiver === null ? {} : { caregiver }),
        });
    }, [
        asksCaregiver,
        asksClient,
        caregiverCustom,
        caregiverValue,
        clientCustom,
        clientValue,
        onChange,
    ]);

    return (
        <div className="mt-2 flex flex-col gap-2">
            {picker.rates.map((rate) => {
                const key = rate.key;
                const isClient = key === 'client';
                const options = optionsByKey[key];
                const query = isClient ? clientRates : caregiverRates;
                const value = values[key];
                const state = custom[key];
                const chosen = options.find((option) => option.value === value);
                const blocked = !isClient && (clientRateId ?? null) === null;
                // A custom amount replaces the rate, so nothing to ask.
                const replacedByAmount = state.on;
                const pending =
                    !blocked &&
                    query.data === undefined &&
                    !query.hasRequestFailed;
                const inactive = disabled || blocked || options.length === 0;
                return (
                    <div key={key} className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-muted-foreground text-v3-chat-meta">
                                {rate.label}
                            </span>
                            {rate.allow_custom_amount === true && (
                                <Label
                                    htmlFor={`v3-custom-rate-${key}-${picker.shift_id ?? picker.client_id}`}
                                    className="text-muted-foreground text-v3-chat-meta flex items-center gap-1.5 font-normal"
                                >
                                    <Checkbox
                                        id={`v3-custom-rate-${key}-${picker.shift_id ?? picker.client_id}`}
                                        checked={state.on}
                                        disabled={disabled || blocked}
                                        onCheckedChange={(checked) => {
                                            const on = checked === true;
                                            setCustom((current) => ({
                                                ...current,
                                                [key]: {
                                                    on,
                                                    type: on
                                                        ? (toCustomCaregiverRateType(
                                                              parse(value)
                                                                  ?.type,
                                                          ) ?? '1')
                                                        : current[key].type,
                                                    amount: '',
                                                },
                                            }));
                                            if (on) {
                                                setValues((current) => ({
                                                    ...current,
                                                    [key]: '',
                                                }));
                                            }
                                        }}
                                    />
                                    Custom amount
                                </Label>
                            )}
                        </div>
                        {replacedByAmount ? (
                            <div className="grid grid-cols-[minmax(6.5rem,0.85fr)_minmax(7rem,1fr)] gap-2">
                                <Select
                                    value={state.type}
                                    onValueChange={(next) => {
                                        const rateType =
                                            toCustomCaregiverRateType(next);
                                        if (rateType !== null) {
                                            setCustom((current) => ({
                                                ...current,
                                                [key]: {
                                                    ...current[key],
                                                    type: rateType,
                                                },
                                            }));
                                        }
                                    }}
                                    disabled={disabled}
                                >
                                    <SelectTrigger
                                        className="text-v3-chat h-8 w-full"
                                        aria-label={`${AMOUNT_LABELS[key]} type`}
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CUSTOM_CAREGIVER_RATE_TYPES.map(
                                            (option) => (
                                                <SelectItem
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </SelectItem>
                                            ),
                                        )}
                                    </SelectContent>
                                </Select>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    disabled={disabled}
                                    value={state.amount}
                                    onChange={(event) =>
                                        setCustom((current) => ({
                                            ...current,
                                            [key]: {
                                                ...current[key],
                                                amount: event.target.value,
                                            },
                                        }))
                                    }
                                    placeholder={AMOUNT_LABELS[key]}
                                    aria-label={AMOUNT_LABELS[key]}
                                    className="text-v3-chat h-8"
                                />
                            </div>
                        ) : (
                            <Combobox
                                open={open[key]}
                                onOpenChange={(next) =>
                                    setOpen((current) => ({
                                        ...current,
                                        [key]: next,
                                    }))
                                }
                                displayValue={chosen?.label}
                                disabled={inactive}
                                searchValue={search[key]}
                                onSearchValueChange={(next) =>
                                    setSearch((current) => ({
                                        ...current,
                                        [key]: next,
                                    }))
                                }
                                searchPlaceholder={`Search ${rate.label.toLowerCase()}s…`}
                                placeholder={
                                    query.isLoading || pending
                                        ? 'Loading rates…'
                                        : blocked
                                          ? 'Pick a client rate first'
                                          : options.length === 0
                                            ? 'No rates available'
                                            : 'Choose a rate'
                                }
                                triggerClassName="text-v3-chat text-left overflow-hidden"
                                commandListProps={{ className: 'max-h-64' }}
                            >
                                <CommandEmpty className="text-muted-foreground text-v3-chat-meta py-6 text-center">
                                    No rates found
                                </CommandEmpty>
                                {options.map((option) => (
                                    <CommandItem
                                        key={option.value}
                                        value={option.label}
                                        onSelect={() => {
                                            setValues((current) => ({
                                                ...current,
                                                [key]: option.value,
                                            }));
                                            setOpen((current) => ({
                                                ...current,
                                                [key]: false,
                                            }));
                                        }}
                                        className="text-v3-chat py-2 text-left"
                                    >
                                        {option.label}
                                        {option.detail !== null && (
                                            <span className="text-muted-foreground ml-1.5">
                                                {option.detail}
                                            </span>
                                        )}
                                    </CommandItem>
                                ))}
                            </Combobox>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
